import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Tooltip, 
  Select,
  Badge,
  Modal
} from 'antd';
import { 
  FaCreditCard, 
  FaSearch, 
  FaSync, 
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
  FaBuilding
} from 'react-icons/fa';
import { BiCheckCircle } from "react-icons/bi";
import { HiOutlineCreditCard, HiOutlineCalendar } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import dayjs from 'dayjs';
import './Pagamentos.css';

const { Search } = Input;
const { Option } = Select;

const Pagamentos = () => {
  const [pagamentos, setPagamentos] = useState([]);
  const [filteredPagamentos, setFilteredPagamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [tipoFilter, setTipoFilter] = useState('');
  const [periodoFilter, setPeriodoFilter] = useState('');
  const [parceiroFilter, setParceiroFilter] = useState('');
  const [uniqueTipos, setUniqueTipos] = useState([]);
  const [uniquePeriodos, setUniquePeriodos] = useState([]);
  const [uniqueParceiros, setUniqueParceiros] = useState([]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  // Estatísticas
  const totalPagamentos = useMemo(() => pagamentos.length, [pagamentos]);
  const pagamentosPorPeriodo = useMemo(() => {
    const counts = {};
    pagamentos.forEach(pag => {
      const periodo = pag.dias || 'NÃO DEFINIDO';
      counts[periodo] = (counts[periodo] || 0) + 1;
    });
    return counts;
  }, [pagamentos]);

  const pagamentosPorTipo = useMemo(() => {
    const counts = {};
    pagamentos.forEach(pag => {
      const tipo = pag.tipo || 'NÃO DEFINIDO';
      counts[tipo] = (counts[tipo] || 0) + 1;
    });
    return counts;
  }, [pagamentos]);

  const pagamentosPorParceiro = useMemo(() => {
    const counts = {};
    pagamentos.forEach(pag => {
      const parceiro = pag.nome_parceiro || 'SEM PARCEIRO';
      counts[parceiro] = (counts[parceiro] || 0) + 1;
    });
    return counts;
  }, [pagamentos]);

  const sms_error = (sms) => {
    toast.error(sms, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const sms_success = (sms) => {
    toast.success(sms, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const fetchPagamentos = async () => {
    setLoading(true);
    try {
      const response = await Api.ListPagamento();
      
      if (response.success && Array.isArray(response.licencas)) {
        const formattedPagamentos = response.licencas.map((item, index) => ({
          key: item.id,
          id: item.id,
          cliente: item.Nome,
          dias: item.Periodo,
          tipo: item.Tipo,
          keyIn: item.KeyIn || new Date().toISOString().split('T')[0],
          nome_parceiro: item.nome_parceiro || 'Sem parceiro',
          numero: index + 1,
          status: 'pendente',
          dataSolicitacao: item.dataSolicitacao || new Date().toISOString().split('T')[0]
        }));
        
        setPagamentos(formattedPagamentos);
        setFilteredPagamentos(formattedPagamentos);
        
        // Extrair dados únicos para filtros
        const tipos = [...new Set(response.licencas
          .filter(item => item.Tipo)
          .map(item => item.Tipo))];
        setUniqueTipos(tipos);
        
        const periodos = [...new Set(response.licencas
          .filter(item => item.Periodo)
          .map(item => item.Periodo))];
        setUniquePeriodos(periodos);

        const parceiros = [...new Set(response.licencas
          .filter(item => item.nome_parceiro)
          .map(item => item.nome_parceiro))];
        setUniqueParceiros(parceiros);
      } else {
        sms_error('Formato de resposta inválido');
      }
    } catch (error) {
      sms_error('Erro ao buscar pagamentos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPagamentos();
  }, []);

  // Função de busca global
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      applyFilters({}, tipoFilter, periodoFilter, parceiroFilter);
      return;
    }
    
    const filtered = pagamentos.filter(item =>
      item.cliente?.toLowerCase().includes(value.toLowerCase()) ||
      item.nome_parceiro?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPagamentos(filtered);
  };

  // Aplicar todos os filtros
  const applyFilters = (filters, tipo, periodo, parceiro) => {
    let filtered = [...pagamentos];
    
    // Aplicar filtros de campos
    Object.entries(filters).forEach(([key, val]) => {
      if (val) {
        filtered = filtered.filter(item => 
          item[key]?.toString().toLowerCase().includes(val.toLowerCase())
        );
      }
    });
    
    // Aplicar filtro de tipo
    if (tipo) {
      filtered = filtered.filter(item => 
        item.tipo?.toLowerCase() === tipo.toLowerCase()
      );
    }
    
    // Aplicar filtro de período
    if (periodo) {
      filtered = filtered.filter(item => 
        item.dias?.toLowerCase() === periodo.toLowerCase()
      );
    }
    
    // Aplicar filtro de parceiro
    if (parceiro) {
      filtered = filtered.filter(item => 
        item.nome_parceiro?.toLowerCase() === parceiro.toLowerCase()
      );
    }
    
    setFilteredPagamentos(filtered);
  };

  // Filtrar por campo específico
  const handleFilter = (field, value) => {
    const newFilters = { ...activeFilters, [field]: value };
    setActiveFilters(newFilters);
    applyFilters(newFilters, tipoFilter, periodoFilter, parceiroFilter);
  };

  // Filtrar por tipo
  const handleTipoFilter = (value) => {
    setTipoFilter(value);
    applyFilters(activeFilters, value, periodoFilter, parceiroFilter);
  };

  // Filtrar por período
  const handlePeriodoFilter = (value) => {
    setPeriodoFilter(value);
    applyFilters(activeFilters, tipoFilter, value, parceiroFilter);
  };

  // Filtrar por parceiro
  const handleParceiroFilter = (value) => {
    setParceiroFilter(value);
    applyFilters(activeFilters, tipoFilter, periodoFilter, value);
  };

  // Limpar filtros
  const clearFilters = () => {
    setActiveFilters({});
    setTipoFilter('');
    setPeriodoFilter('');
    setParceiroFilter('');
    setSearchText('');
    setFilteredPagamentos(pagamentos);
  };

  // Função para confirmar pagamento
  const handleConfirmPayment = async () => {
    if (!selectedPayment) return;
    
    try {
      const costumerData = {
        id: selectedPayment.key,
      };

      const result = await Api.AlterLicenca(costumerData);
      if (result.success) {
        sms_success('Pagamento confirmado com sucesso!');
        fetchPagamentos();
      } else {
        sms_error(result.message || 'Erro ao confirmar pagamento');
      }
    } catch (error) {
      sms_error('Erro ao confirmar pagamento: ' + error.message);
    } finally {
      setConfirmModalVisible(false);
      setSelectedPayment(null);
    }
  };

  // Colunas base
  const baseColumns = [
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      width: '25%',
      render: (text) => (
        <div className="client-cell">
          <FaUser className="client-icon" />
          <span className="client-name">{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por cliente"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('cliente', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('cliente', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('cliente', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'KeyIn',
      dataIndex: 'keyIn',
      key: 'keyIn',
      width: '15%',
      align: 'center',
      render: (date) => (
        <div className="date-cell">
          <HiOutlineCalendar className="date-icon" />
          <span>{dayjs(date).format('DD/MM/YYYY')}</span>
        </div>
      ),
      sorter: (a, b) => dayjs(a.keyIn).unix() - dayjs(b.keyIn).unix(),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por data (DD/MM/YYYY)"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('keyIn', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('keyIn', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('keyIn', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Período',
      dataIndex: 'dias',
      key: 'dias',
      width: '15%',
      align: 'center',
      render: (periodo) => (
        <Tag 
          color={getPeriodoColor(periodo)} 
          className="periodo-tag"
        >
          {periodo || 'Não definido'}
        </Tag>
      ),
      filters: uniquePeriodos.map(periodo => ({ text: periodo, value: periodo })),
      onFilter: (value, record) => record.dias === value,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: '15%',
      align: 'center',
      render: (tipo) => (
        <Tag color={tipo === 'Multi' ? 'blue' : 'purple'}>
          {tipo}
        </Tag>
      ),
      filters: uniqueTipos.map(tipo => ({ text: tipo, value: tipo })),
      onFilter: (value, record) => record.tipo === value,
    },
    {
      title: 'Parceiro',
      dataIndex: 'nome_parceiro',
      key: 'nome_parceiro',
      width: '20%',
      render: (parceiro) => (
        <div className="partner-cell">
          <FaBuilding className="partner-icon" />
          <span className="partner-name">{parceiro}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por parceiro"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('nome_parceiro', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('nome_parceiro', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('nome_parceiro', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
  ];

  // Adicionar coluna de ação apenas se user.id === 1
  if (user.id === 1) {
    baseColumns.push({
      title: 'Ação',
      key: 'acao',
      width: '10%',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Confirmar pagamento">
          <Button
            type="primary"
           
            icon={<BiCheckCircle />}
            size="small"
            onClick={() => {
              setSelectedPayment(record);
              setConfirmModalVisible(true);
            }}
            style={{ 
              backgroundColor: '#52c41a',
              borderColor: '#52c41a'
            }}
          />
        </Tooltip>
      ),
    });
  }

  // Função para obter cor baseada no período
  const getPeriodoColor = (periodo) => {
    const colors = {
      'MENSAL': 'blue',
      'TRIMESTRAL': 'green',
      'SEMESTRAL': 'orange',
      'ANUAL': 'red',
      'PERÍODO DESCONHECIDO': 'default'
    };
    return colors[periodo] || 'default';
  };

  // Calcular estatísticas por período
  const calculatePeriodStats = () => {
    const stats = {
      MENSAL: 0,
      TRIMESTRAL: 0,
      SEMESTRAL: 0,
      ANUAL: 0,
      OUTROS: 0
    };

    pagamentos.forEach(pag => {
      const periodo = pag.dias;
      if (stats.hasOwnProperty(periodo)) {
        stats[periodo]++;
      } else {
        stats.OUTROS++;
      }
    });

    return stats;
  };

  const periodStats = calculatePeriodStats();

  return (
    <div className="pagamentos-container">
      <ToastContainer />
      
 

      {/* Cards de estatísticas */}
      <Row gutter={16} className="stats-row">
        <Col xs={24} md={8}>
          <Card className="stat-card total-card">
            <Statistic
              title="Total de Pagamentos"
              value={totalPagamentos}
              prefix={<FaCreditCard />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div className="stat-trend">
              <span>Pagamentos pendentes</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card monthly-card">
            <Statistic
              title="Mensais"
              value={periodStats.MENSAL}
              prefix={<FaCalendarAlt />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div className="stat-trend">
              <span>Pagamentos mensais</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="stat-card partner-card">
            <Statistic
              title="Parceiros"
              value={uniqueParceiros.length}
              prefix={<FaBuilding />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div className="stat-trend">
              <span>Parceiros ativos</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Distribuição por Período */}
      <Card className="distribution-card" style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaClock /> Distribuição por Período
        </h3>
        <Row gutter={16}>
          {Object.entries(periodStats).map(([periodo, quantidade]) => (
            quantidade > 0 && (
              <Col xs={24} sm={12} md={4} key={periodo}>
                <div className="period-distribution">
                  <div className="period-name">{periodo}</div>
                  <div className="period-count">{quantidade}</div>
                  <div className="period-progress">
                    <div className="progress-bar" style={{
                      width: `${(quantidade / totalPagamentos) * 100}%`,
                      backgroundColor: getPeriodoColor(periodo)
                    }}></div>
                  </div>
                </div>
              </Col>
            )
          ))}
        </Row>
      </Card>

      {/* Barra de ferramentas com filtros */}
      <Card className="toolbar-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={8}>
            <Search
              placeholder="Buscar por cliente ou parceiro..."
              prefix={<FaSearch />}
              enterButton="Buscar"
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          
          <Col xs={24} md={12} lg={4}>
            <div className="filter-group">
              <label style={{ marginBottom: '4px', color: '#666' }}>
                <FaBox style={{ marginRight: '4px' }} />
                Tipo:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os tipos"
                value={tipoFilter || undefined}
                onChange={handleTipoFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">Todos</Option>
                {uniqueTipos.map((tipo, index) => (
                  <Option key={index} value={tipo}>
                    {tipo}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={4}>
            <div className="filter-group">
              <label style={{ marginBottom: '4px', color: '#666' }}>
                <FaCalendarAlt style={{ marginRight: '4px' }} />
                Período:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os períodos"
                value={periodoFilter || undefined}
                onChange={handlePeriodoFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">Todos</Option>
                {uniquePeriodos.map((periodo, index) => (
                  <Option key={index} value={periodo}>
                    {periodo}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={4}>
            <div className="filter-group">
              <label style={{ marginBottom: '4px', color: '#666' }}>
                <FaBuilding style={{ marginRight: '4px' }} />
                Parceiro:
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Todos os parceiros"
                value={parceiroFilter || undefined}
                onChange={handleParceiroFilter}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="">Todos</Option>
                {uniqueParceiros.map((parceiro, index) => (
                  <Option key={index} value={parceiro}>
                    {parceiro}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={4} style={{ textAlign: 'right' }}>
            <Button
              type="default"
              onClick={clearFilters}
              style={{ marginTop: '24px' }}
            >
              Limpar Filtros
            </Button>
          </Col>
        </Row>
        
        {/* Filtros ativos */}
        {(Object.keys(activeFilters).length > 0 || tipoFilter || periodoFilter || parceiroFilter) && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            {Object.entries(activeFilters).map(([key, value]) => (
              value && (
                <Tag 
                  key={key} 
                  closable 
                  onClose={() => handleFilter(key, '')}
                  color="blue"
                >
                  {key === 'cliente' ? 'Cliente' : 
                   key === 'keyIn' ? 'Data' : 
                   key === 'nome_parceiro' ? 'Parceiro' : key}: {value}
                </Tag>
              )
            ))}
            {tipoFilter && (
              <Tag 
                closable 
                onClose={() => handleTipoFilter('')}
                color="purple"
              >
                Tipo: {tipoFilter}
              </Tag>
            )}
            {periodoFilter && (
              <Tag 
                closable 
                onClose={() => handlePeriodoFilter('')}
                color="green"
              >
                Período: {periodoFilter}
              </Tag>
            )}
            {parceiroFilter && (
              <Tag 
                closable 
                onClose={() => handleParceiroFilter('')}
                color="cyan"
              >
                Parceiro: {parceiroFilter}
              </Tag>
            )}
          </div>
        )}
      </Card>

      {/* Tabela de pagamentos */}
      <div className="table-responsive-wrapper">
        <Card className="table-card">
          <Table
            columns={baseColumns}
            dataSource={filteredPagamentos}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} pagamentos`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            rowClassName={(record, index) => 
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            locale={{
              emptyText: searchText || Object.keys(activeFilters).length > 0 || tipoFilter || periodoFilter || parceiroFilter
                ? 'Nenhum pagamento encontrado com os filtros atuais' 
                : 'Nenhum pagamento pendente',
            }}
          />
        </Card>
      </div>

      {/* Modal de confirmação */}
      <Modal
        title="Confirmar Pagamento"
        visible={confirmModalVisible}
        onOk={handleConfirmPayment}
        onCancel={() => {
          setConfirmModalVisible(false);
          setSelectedPayment(null);
        }}
        okText="Confirmar"
        cancelText="Cancelar"
        okType="primary"
        okButtonProps={{ icon: <BiCheckCircle /> }}
      >
        <div className="confirm-modal-content">
          <div className="warning-icon">💳</div>
          <p>
            Tem certeza que deseja confirmar o pagamento para o cliente 
            <strong> {selectedPayment?.cliente}</strong>?
          </p>
          <p className="payment-details">
            <strong>Cliente:</strong> {selectedPayment?.cliente}<br/>
            <strong>KeyIn:</strong> {selectedPayment?.keyIn && dayjs(selectedPayment.keyIn).format('DD/MM/YYYY')}<br/>
            <strong>Período:</strong> {selectedPayment?.dias}<br/>
            <strong>Tipo:</strong> {selectedPayment?.tipo}<br/>
            <strong>Parceiro:</strong> {selectedPayment?.nome_parceiro}
          </p>
          <p className="confirm-warning">
            Esta ação marcará o pagamento como confirmado.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Pagamentos;